import { addDays, addMonths, parseISO, startOfMonth } from "date-fns"
import { toISODateString } from "@/utils"
import type { CreditMemoPreviewLine, PostingDateOverride } from "@/api/accounting"

/**
 * Xero period locks vs. our execution dates.
 *
 * A deduction's execution date comes from the distributor's backup, so a check paid in August
 * routinely carries June-dated lines. When the brand has closed June in Xero, the credit note is
 * rejected: "The document date cannot be before the period lock date". Xero has no separate posting
 * date — a document's Date *is* its GL period — and the API gets no advisor override, so the only
 * way into an open period is a different date on the note.
 *
 * These mirror `server/src/accounting/xero_period_lock.rs`. The server resolves the same rule at
 * apply time and clamps anything still locked, so the UI can never send a date Xero would refuse.
 *
 * Dates are ISO `yyyy-MM-dd` throughout, which compares correctly as plain strings and keeps this
 * free of timezone drift.
 */

/** Whether Xero would reject a document dated `date`. The lock is inclusive of its own date. */
export function is_locked(date?: string, lock_date?: string): boolean {
  if (!date || !lock_date) return false
  return date <= lock_date
}

/**
 * The earliest date Xero will accept at all — the day after the lock. This is the floor on what the
 * user may pick by hand, not the default; picking a date in the part-locked month is allowed.
 */
export function first_postable_date(lock_date: string): string {
  return toISODateString(addDays(parseISO(lock_date), 1))
}

/**
 * The date a locked line posts on by default: the first day of the first *fully open month*.
 *
 * A lock rarely lands on a month end — Compostic's sat at 18 May — so "the day after the lock" would
 * drop entries into the middle of a half-closed month, and the payment date would push them further
 * out than they need to go. The first of the next month is the least-distorting landing spot that is
 * unambiguously inside an open period, and it keeps a whole check's moved notes on one boundary.
 */
export function first_open_month_start(lock_date: string): string {
  return toISODateString(startOfMonth(addMonths(parseISO(lock_date), 1)))
}

/**
 * The posting date for one line, or undefined when it books on its execution date as usual.
 *
 * An override wins over the default but is still clamped — an override inside the locked period
 * would only reproduce the same Xero rejection.
 */
export function resolve_posting_date(
  execution_date?: string,
  lock_date?: string,
  override?: string
): string | undefined {
  // No execution date means the note already books on the memo's payment date, which the user sets
  // directly via the "Payment date" field.
  if (!execution_date) return undefined

  let posting = override || execution_date
  if (is_locked(posting, lock_date) && lock_date) {
    posting = first_open_month_start(lock_date)
  }
  return posting === execution_date ? undefined : posting
}

/** The distinct execution dates on these lines that Xero's lock would reject, in first-seen order. */
export function locked_execution_dates(lines: CreditMemoPreviewLine[], lock_date?: string): string[] {
  const locked: string[] = []
  for (const line of lines) {
    if (is_locked(line.execution_date, lock_date) && line.execution_date && !locked.includes(line.execution_date)) {
      locked.push(line.execution_date)
    }
  }
  return locked
}

/**
 * The posting dates to send with an apply, one row per distinct execution date that moves.
 *
 * Sending the resolved default alongside any override keeps the request explicit about where every
 * note lands, rather than relying on the server and the UI agreeing on the default by coincidence.
 */
export function build_posting_date_overrides(
  lines: CreditMemoPreviewLine[],
  lock_date?: string,
  overrides: Record<string, string> = {}
): PostingDateOverride[] {
  const rows: PostingDateOverride[] = []
  for (const line of lines) {
    const execution_date = line.execution_date
    if (!execution_date || rows.some((r) => r.execution_date === execution_date)) continue
    const posting_date = resolve_posting_date(execution_date, lock_date, overrides[execution_date])
    if (posting_date) rows.push({ execution_date, posting_date })
  }
  return rows
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function line(execution_date?: string): CreditMemoPreviewLine {
    return { gl_account_id: "400", gl_account_name: "Spoilage", amount: 27.37, execution_date }
  }

  describe("resolve_posting_date", () => {
    it("leaves a line in its own period when that period is open", () => {
      expect(resolve_posting_date("2026-06-12", undefined)).toBeUndefined()
      expect(resolve_posting_date("2026-06-12", "2026-05-31")).toBeUndefined()
    })

    it("moves a locked line to the first day of the first open month", () => {
      expect(resolve_posting_date("2026-06-12", "2026-06-30")).toBe("2026-07-01")
    })

    it("skips a half-closed month when the lock lands mid-month", () => {
      expect(resolve_posting_date("2026-04-20", "2026-05-18")).toBe("2026-06-01")
    })

    it("treats the lock date itself as locked", () => {
      expect(resolve_posting_date("2026-06-30", "2026-06-30")).toBe("2026-07-01")
    })

    it("honours an override, and clamps one that is itself locked", () => {
      expect(resolve_posting_date("2026-06-12", "2026-06-30", "2026-08-15")).toBe("2026-08-15")
      expect(resolve_posting_date("2026-06-12", "2026-06-30", "2026-06-05")).toBe("2026-07-01")
    })

    it("lets an override land in the part-locked month the default skips", () => {
      expect(resolve_posting_date("2026-04-20", "2026-05-18", "2026-05-19")).toBe("2026-05-19")
    })

    it("is not a move when the override equals the execution date", () => {
      expect(resolve_posting_date("2026-06-12", undefined, "2026-06-12")).toBeUndefined()
    })

    it("leaves a line with no execution date to the payment date", () => {
      expect(resolve_posting_date(undefined, "2026-06-30")).toBeUndefined()
    })
  })

  describe("first_open_month_start", () => {
    it("crosses a year boundary", () => {
      expect(first_open_month_start("2026-12-31")).toBe("2027-01-01")
    })

    it("does not skip a month when the lock is on a long month end", () => {
      expect(first_open_month_start("2026-01-31")).toBe("2026-02-01")
    })

    it("is always strictly after the lock, so it is always postable", () => {
      for (const lock of ["2026-01-01", "2026-02-28", "2026-05-18", "2026-12-31"]) {
        expect(first_open_month_start(lock) > lock).toBe(true)
      }
    })
  })

  describe("first_postable_date", () => {
    it("is the day after the lock", () => {
      expect(first_postable_date("2026-05-18")).toBe("2026-05-19")
      expect(first_postable_date("2026-12-31")).toBe("2027-01-01")
    })
  })

  describe("locked_execution_dates", () => {
    it("lists distinct locked dates in first-seen order", () => {
      const lines = [line("2026-06-29"), line("2026-06-12"), line("2026-06-29"), line("2026-07-20"), line(undefined)]
      expect(locked_execution_dates(lines, "2026-06-30")).toEqual(["2026-06-29", "2026-06-12"])
    })

    it("is empty when no lock is set", () => {
      expect(locked_execution_dates([line("2026-06-12")], undefined)).toEqual([])
    })
  })

  describe("build_posting_date_overrides", () => {
    it("sends one row per moving date and nothing for open ones", () => {
      const lines = [line("2026-06-12"), line("2026-06-12"), line("2026-07-20")]
      expect(build_posting_date_overrides(lines, "2026-06-30")).toEqual([
        { execution_date: "2026-06-12", posting_date: "2026-07-01" },
      ])
    })

    it("carries a user override for every line sharing that date", () => {
      const lines = [line("2026-06-12"), line("2026-06-29")]
      expect(build_posting_date_overrides(lines, "2026-06-30", { "2026-06-12": "2026-08-15" })).toEqual([
        { execution_date: "2026-06-12", posting_date: "2026-08-15" },
        { execution_date: "2026-06-29", posting_date: "2026-07-01" },
      ])
    })

    it("sends nothing when no period is locked", () => {
      expect(build_posting_date_overrides([line("2026-06-12")], undefined)).toEqual([])
    })
  })
}
